import React from "react";
import { ShieldCheck, Phone, ExternalLink } from "lucide-react";
import { SEO } from "../components/SEO";

const RESOURCES = [
    {
        name: "SAMHSA National Helpline",
        detail: "1-800-662-HELP (4357) · Free, confidential, 24/7",
        href: "tel:18006624357",
    },
    {
        name: "NIAAA — Rethinking Drinking",
        detail: "Tools, tips and what's considered low-risk drinking.",
        href: "https://www.rethinkingdrinking.niaaa.nih.gov/",
    },
    {
        name: "Drinkchat",
        detail: "Trained advisors via live chat.",
        href: "https://www.drinkchat.co.uk/",
    },
    {
        name: "Foundation for Advancing Alcohol Responsibility",
        detail: "Education on responsible decisions.",
        href: "https://www.responsibility.org/",
    },
];

export default function Responsible() {
    return (
        <>
            <SEO
                title="Responsible Drinking"
                description="Liquor Studio's commitment to responsible drinking. Missouri 21+ law, ID policy, and helpful resources including SAMHSA and NIAAA."
                path="/responsible-drinking"
            />

            <section
                style={{ paddingTop: 160, paddingBottom: 64, borderBottom: "1px solid var(--ls-border)" }}
                data-testid="responsible-header"
            >
                <div className="ls-container max-w-4xl">
                    <span className="eyebrow">Responsible Drinking</span>
                    <h1
                        className="font-display mt-5 text-white"
                        style={{
                            fontSize: "clamp(40px, 6vw, 72px)",
                            fontWeight: 500,
                            lineHeight: 1.05,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        We card.<br />
                        <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                            We care.
                        </em>
                    </h1>
                    <p
                        className="mt-6"
                        style={{ color: "var(--ls-text-secondary)", fontSize: 18, lineHeight: 1.7 }}
                    >
                        Selling alcohol is a privilege — not a right. We take it seriously.
                        Here's how, and where to go if you or someone you love needs help.
                    </p>
                </div>
            </section>

            <section className="section-y" data-testid="responsible-content">
                <div className="ls-container max-w-4xl">
                    {/* Missouri law block */}
                    <article
                        style={{
                            background: "var(--ls-panel)",
                            border: "1px solid var(--ls-gold)",
                            borderRadius: 6,
                            padding: 32,
                        }}
                    >
                        <ShieldCheck size={26} style={{ color: "var(--ls-gold)" }} />
                        <h2
                            className="font-display mt-4 text-white"
                            style={{ fontSize: 30, fontWeight: 500 }}
                        >
                            We ID anyone who appears under 40.
                        </h2>
                        <p
                            className="mt-4"
                            style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.7 }}
                        >
                            Missouri state law (RSMo § 311.310) prohibits the sale of intoxicating
                            liquor to any person under the age of 21. Liquor Studio enforces this
                            without exception. We also refuse service to anyone who appears intoxicated.
                            Acceptable IDs include a valid US driver's license, state-issued ID, US
                            passport, or US military ID.
                        </p>
                    </article>

                    {/* What we won't do */}
                    <div className="mt-12">
                        <h2 className="font-display text-white" style={{ fontSize: 32, fontWeight: 500 }}>
                            What we won't do
                        </h2>
                        <ul
                            className="mt-6 space-y-3 text-base"
                            style={{ color: "var(--ls-text-secondary)", lineHeight: 1.7 }}
                        >
                            <li>— Sell to anyone under 21, ever.</li>
                            <li>— Sell to anyone who appears intoxicated.</li>
                            <li>— Knowingly sell to someone purchasing on behalf of a minor.</li>
                            <li>— Skip the ID check, even on regulars.</li>
                            <li>— Ship alcohol. All sales are in-store or local pickup only.</li>
                        </ul>
                    </div>

                    {/* Resources */}
                    <div className="mt-16">
                        <h2 className="font-display text-white" style={{ fontSize: 32, fontWeight: 500 }}>
                            Resources — confidential help, 24/7
                        </h2>
                        <p className="mt-3" style={{ color: "var(--ls-text-secondary)" }}>
                            If you or someone you know is struggling with alcohol use, these are
                            the resources we trust.
                        </p>

                        <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {RESOURCES.map((r) => (
                                <li key={r.name}>
                                    <a
                                        href={r.href}
                                        target={r.href.startsWith("http") ? "_blank" : undefined}
                                        rel={r.href.startsWith("http") ? "noopener noreferrer" : undefined}
                                        className="block p-6"
                                        style={{
                                            background: "var(--ls-panel)",
                                            border: "1px solid var(--ls-border)",
                                            borderRadius: 6,
                                            transition: "border-color 0.2s ease",
                                        }}
                                        data-testid={`resource-${r.name.replace(/\s+/g, "-").toLowerCase()}`}
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <h3 className="font-display text-white" style={{ fontSize: 18, fontWeight: 500 }}>
                                                {r.name}
                                            </h3>
                                            {r.href.startsWith("tel") ? (
                                                <Phone size={16} style={{ color: "var(--ls-gold)" }} />
                                            ) : (
                                                <ExternalLink size={16} style={{ color: "var(--ls-gold)" }} />
                                            )}
                                        </div>
                                        <p
                                            className="mt-2 text-sm"
                                            style={{ color: "var(--ls-text-secondary)", lineHeight: 1.5 }}
                                        >
                                            {r.detail}
                                        </p>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <p
                        className="mt-16 text-sm"
                        style={{ color: "var(--ls-text-muted)", lineHeight: 1.6, fontStyle: "italic" }}
                    >
                        Please drink responsibly. Pregnant? Don't drink. Driving? Don't drink.
                        Liquor Studio is a licensed Missouri retailer.
                    </p>
                </div>
            </section>
        </>
    );
}
